"use client";

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import { CodeBlockContent } from "@/components/ai-elements/code-block";
import { MessageResponse, type MessageResponseProps } from "@/components/ai-elements/message";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EyeIcon } from "@/components/ui/eye";
import { MessageCircleIcon } from "@/components/ui/message-circle";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";
import { ArrowUpIcon, CodeXmlIcon, Columns2, Maximize2 } from "lucide-react";
import { toast } from "sonner";

export type MarkdownPreviewMode = "markdown" | "preview" | "split";

/** Shared document layout for meeting minutes and chat Markdown previews. */
export const MARKDOWN_DOCUMENT_PREVIEW_CLASS_NAME =
  "minutes-prose mx-auto h-auto min-h-0 w-full max-w-3xl overflow-visible border-0 bg-transparent px-2 pt-16 pb-6 sm:px-8";

type MarkdownPreviewToggleProps = {
  mode: MarkdownPreviewMode | null;
  onModeChange: (mode: MarkdownPreviewMode) => void;
  ariaLabel?: string;
  markdownAriaLabel?: string;
  previewAriaLabel?: string;
  splitAriaLabel?: string;
  hideSplit?: boolean;
  className?: string;
};

export function MarkdownPreviewToolbar({ children }: { children: React.ReactNode }) {
  return (
    <div className="pointer-events-none absolute right-3 top-3 z-10">
      <div className="pointer-events-auto flex items-center gap-0.5 rounded-lg border border-border bg-card/95 p-0.5 shadow-sm backdrop-blur [&>button]:size-7 [&>a]:size-7 [&_svg]:size-3.5! [&>[data-slot=toggle-group]]:h-7 [&_[data-slot=toggle-group-item]]:h-6 [&_[data-slot=toggle-group-item]]:min-w-7 [&_[data-slot=toggle-group-item]]:px-1.5">
        {children}
      </div>
    </div>
  );
}

export function MarkdownPreviewToggle({
  mode,
  onModeChange,
  ariaLabel = "Switch markdown preview mode",
  markdownAriaLabel = "Show markdown",
  previewAriaLabel = "Show preview",
  splitAriaLabel = "Split view",
  hideSplit = false,
  className,
}: MarkdownPreviewToggleProps) {
  const indicatorId = useId();
  const reduceMotion = useReducedMotion();
  const itemClassName =
    "relative isolate h-7 min-w-8 rounded-lg border border-transparent px-2 text-muted-foreground data-[state=on]:bg-transparent data-[state=on]:text-foreground aria-pressed:bg-transparent";
  const selectionIndicator = (
    <motion.span
      aria-hidden="true"
      className="pointer-events-none absolute -inset-px -z-10 rounded-lg border border-border bg-background shadow-sm"
      layoutId={indicatorId}
      transition={reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 500, damping: 35 }}
    />
  );

  return (
    <ToggleGroup
      aria-label={ariaLabel}
      className={cn("rounded-xl bg-muted p-0.5", className)}
      onValueChange={(value) => {
        if (value === "markdown" || value === "preview" || value === "split") {
          onModeChange(value);
        }
      }}
      size="sm"
      spacing={1}
      type="single"
      value={mode ?? ""}
      variant="default"
    >
      <ToggleGroupItem aria-label={previewAriaLabel} title={previewAriaLabel} value="preview" className={itemClassName}>
        {mode === "preview" && selectionIndicator}
        <EyeIcon className="text-current" size={16} />
        <span className="sr-only">Preview</span>
      </ToggleGroupItem>
      {!hideSplit && (
        <ToggleGroupItem aria-label={splitAriaLabel} value="split" className={itemClassName}>
          {mode === "split" && selectionIndicator}
          <Columns2 size={16} />
          <span className="sr-only">Split</span>
        </ToggleGroupItem>
      )}
      <ToggleGroupItem aria-label={markdownAriaLabel} title={markdownAriaLabel} value="markdown" className={itemClassName}>
        {mode === "markdown" && selectionIndicator}
        <CodeXmlIcon size={16} />
        <span className="sr-only">Markdown</span>
      </ToggleGroupItem>
    </ToggleGroup>
  );
}

type SelectionEditHandler = (selectedText: string, instruction: string) => Promise<boolean>;

type SelectionRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

type SelectionPillState = {
  text: string;
  top: number;
  left: number;
  rects: SelectionRect[];
};

function SelectionEditArea({
  children,
  onSelectionEdit,
}: {
  children: React.ReactNode;
  onSelectionEdit: SelectionEditHandler;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const pillRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [pill, setPill] = useState<SelectionPillState | null>(null);
  const [instruction, setInstruction] = useState("");
  const [sending, setSending] = useState(false);

  const handleMouseUp = useCallback((event: React.MouseEvent) => {
    if (pillRef.current?.contains(event.target as Node)) return;
    const container = containerRef.current;
    const selection = window.getSelection();
    if (!container || !selection || selection.isCollapsed) return;
    const text = selection.toString().trim();
    if (!text) return;
    if (
      !container.contains(selection.anchorNode) ||
      !container.contains(selection.focusNode)
    ) {
      return;
    }
    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const halfPill = 150;
    // Snapshot the selection as our own overlay rects rather than relying on
    // the browser's native selection — focusing the pill's input (or typing
    // into it) collapses the real document selection, but our overlay is
    // immune to that since it's just plain divs kept alive by React state.
    const rects: SelectionRect[] = Array.from(range.getClientRects()).map((clientRect) => ({
      top: clientRect.top - containerRect.top,
      left: clientRect.left - containerRect.left,
      width: clientRect.width,
      height: clientRect.height,
    }));
    selection.removeAllRanges();
    setPill({
      text,
      rects,
      top: Math.max(rect.top - containerRect.top - 44, 4),
      left: Math.min(
        Math.max(rect.left - containerRect.left + rect.width / 2, halfPill),
        Math.max(containerRect.width - halfPill, halfPill)
      ),
    });
    setInstruction("");
  }, []);

  useEffect(() => {
    if (pill) inputRef.current?.focus();
  }, [pill]);

  useEffect(() => {
    if (!pill) return;
    const handleOutside = (event: MouseEvent) => {
      if (pillRef.current?.contains(event.target as Node)) return;
      setPill(null);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [pill]);

  const handleSubmit = useCallback(async () => {
    const trimmed = instruction.trim();
    if (!pill || !trimmed || sending) return;
    setSending(true);
    try {
      const ok = await onSelectionEdit(pill.text, trimmed);
      if (ok) setPill(null);
    } finally {
      setSending(false);
    }
  }, [instruction, onSelectionEdit, pill, sending]);

  return (
    <div ref={containerRef} className="relative" onMouseUp={handleMouseUp}>
      {children}
      {pill
        ? pill.rects.map((rect, index) => (
            <div
              key={index}
              className="pointer-events-none absolute rounded-[3px] bg-primary/25"
              style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
            />
          ))
        : null}
      {pill ? (
        <div
          ref={pillRef}
          className="absolute z-20 -translate-x-1/2"
          style={{ top: pill.top, left: pill.left }}
        >
          <div className="flex items-end gap-1.5 rounded-3xl bg-foreground/95 py-1.5 pl-3 pr-1 shadow-lg backdrop-blur">
            <MessageCircleIcon className="mt-1.5 mb-auto shrink-0 self-start text-background/70" size={14} />
            <textarea
              ref={inputRef}
              className="field-sizing-content max-h-32 w-56 resize-none bg-transparent py-1 text-sm text-background placeholder:text-background/60 focus:outline-none disabled:opacity-60"
              disabled={sending}
              onChange={(event) => setInstruction(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  void handleSubmit();
                } else if (event.key === "Escape") {
                  setPill(null);
                }
              }}
              placeholder="Suggest edit"
              rows={1}
              value={instruction}
            />
            {sending ? (
              <Spinner className="mb-1.5 mr-1.5 size-4 shrink-0 text-background/80" />
            ) : (
              <button
                aria-label="Send change request"
                className="mb-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-full text-background/80 transition-colors hover:bg-background/15 disabled:opacity-40"
                disabled={!instruction.trim()}
                onClick={() => void handleSubmit()}
                type="button"
              >
                <ArrowUpIcon size={15} />
              </button>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

type MarkdownPreviewContentProps = {
  mode: MarkdownPreviewMode;
  value: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  textareaId?: string;
  textareaPlaceholder?: string;
  emptyPreviewText?: string;
  textareaClassName?: string;
  previewClassName?: string;
  /** Render markdown mode as a read-only syntax-highlighted code view with line numbers. */
  markdownCodeView?: boolean;
  codeClassName?: string;
  expandable?: boolean;
  expandTitle?: string;
  onSelectionEdit?: SelectionEditHandler;
  /** Passed straight through to the underlying Streamdown renderer (e.g. a custom `img`). */
  components?: MessageResponseProps["components"];
  /**
   * When set, pasting an image into any of this component's Markdown
   * textareas uploads it (returning its permanent URL) and inserts
   * `![Screenshot](url)` at the cursor — same `uploadFile`-returns-a-URL
   * shape as PromptInput's attachment upload.
   */
  onImagePaste?: (file: File) => Promise<string>;
};

export function MarkdownPreviewContent({
  mode,
  value,
  onChange,
  disabled,
  readOnly,
  textareaId,
  textareaPlaceholder,
  emptyPreviewText = "Nothing to preview yet.",
  textareaClassName,
  previewClassName,
  markdownCodeView = false,
  codeClassName,
  expandable = false,
  expandTitle = "Editor",
  onSelectionEdit,
  components,
  onImagePaste,
}: MarkdownPreviewContentProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const syncing = useRef(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Shared by every Markdown textarea below (primary + the expand dialog's
  // own copies) — uploads a pasted screenshot and inserts it at the cursor.
  const handleImagePaste = useCallback(
    (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
      if (!onImagePaste) return;
      const item = Array.from(event.clipboardData?.items ?? []).find((i) =>
        i.type.startsWith("image/")
      );
      if (!item) return;
      event.preventDefault();
      const file = item.getAsFile();
      if (!file) return;
      const textarea = event.currentTarget;
      const cursor = textarea.selectionStart ?? value.length;
      setUploadingImage(true);
      onImagePaste(file)
        .then((url) => {
          const insertion = `![Screenshot](${url})`;
          onChange?.(`${value.slice(0, cursor)}${insertion}${value.slice(cursor)}`);
        })
        .catch((err) => {
          console.error("[MarkdownPreviewContent] image upload failed:", err);
          toast.error(err instanceof Error ? err.message : "Image upload failed.");
        })
        .finally(() => setUploadingImage(false));
    },
    [onImagePaste, value, onChange]
  );

  const [expandOpen, setExpandOpen] = useState(false);
  const [expandMode, setExpandMode] = useState<MarkdownPreviewMode>("split");
  const expandTextareaRef = useRef<HTMLTextAreaElement>(null);
  const expandPreviewRef = useRef<HTMLDivElement>(null);
  const expandSyncing = useRef(false);

  const syncFromTextarea = useCallback(() => {
    if (syncing.current || !textareaRef.current || !previewRef.current) return;
    syncing.current = true;
    const el = textareaRef.current;
    const pct = el.scrollTop / Math.max(el.scrollHeight - el.clientHeight, 1);
    previewRef.current.scrollTop = pct * Math.max(previewRef.current.scrollHeight - previewRef.current.clientHeight, 0);
    requestAnimationFrame(() => { syncing.current = false; });
  }, []);

  const syncFromPreview = useCallback(() => {
    if (syncing.current || !textareaRef.current || !previewRef.current) return;
    syncing.current = true;
    const el = previewRef.current;
    const pct = el.scrollTop / Math.max(el.scrollHeight - el.clientHeight, 1);
    textareaRef.current.scrollTop = pct * Math.max(textareaRef.current.scrollHeight - textareaRef.current.clientHeight, 0);
    requestAnimationFrame(() => { syncing.current = false; });
  }, []);

  const syncExpandFromTextarea = useCallback(() => {
    if (expandSyncing.current || !expandTextareaRef.current || !expandPreviewRef.current) return;
    expandSyncing.current = true;
    const el = expandTextareaRef.current;
    const pct = el.scrollTop / Math.max(el.scrollHeight - el.clientHeight, 1);
    expandPreviewRef.current.scrollTop = pct * Math.max(expandPreviewRef.current.scrollHeight - expandPreviewRef.current.clientHeight, 0);
    requestAnimationFrame(() => { expandSyncing.current = false; });
  }, []);

  const syncExpandFromPreview = useCallback(() => {
    if (expandSyncing.current || !expandTextareaRef.current || !expandPreviewRef.current) return;
    expandSyncing.current = true;
    const el = expandPreviewRef.current;
    const pct = el.scrollTop / Math.max(el.scrollHeight - el.clientHeight, 1);
    expandTextareaRef.current.scrollTop = pct * Math.max(expandTextareaRef.current.scrollHeight - expandTextareaRef.current.clientHeight, 0);
    requestAnimationFrame(() => { expandSyncing.current = false; });
  }, []);

  let content: React.ReactNode;

  if (mode === "split") {
    content = (
      <div className="flex gap-3">
        <Textarea
          ref={textareaRef}
          onScroll={syncFromTextarea}
          className={cn(
            "h-80 min-h-80 flex-1 overflow-y-auto field-sizing-fixed resize-none",
            textareaClassName
          )}
          disabled={disabled}
          id={textareaId}
          onChange={(event) => onChange?.(event.target.value)}
          onPaste={handleImagePaste}
          placeholder={textareaPlaceholder}
          readOnly={readOnly}
          value={value}
        />
        <div
          ref={previewRef}
          onScroll={syncFromPreview}
          className={cn(
            "h-80 min-h-80 flex-1 overflow-y-auto rounded-xl border border-border bg-muted/30 px-4 py-3",
            previewClassName
          )}
        >
          {value.trim() ? (
            <MessageResponse components={components} mode="static">{value}</MessageResponse>
          ) : (
            <p className="text-sm text-muted-foreground">{emptyPreviewText}</p>
          )}
        </div>
      </div>
    );
  } else if (mode === "markdown") {
    content = markdownCodeView ? (
      <div className={cn("overflow-y-auto", codeClassName)}>
        {value.trim() ? (
          <CodeBlockContent
            className="whitespace-pre-wrap wrap-break-word bg-transparent! py-0 text-xs leading-5 [--shiki-dark-bg:transparent] [&_code]:text-xs [&>code>span]:pl-12 [&>code>span]:-indent-12"
            code={value}
            language="markdown"
            showLineNumbers
          />
        ) : (
          <p className="text-sm text-muted-foreground">{emptyPreviewText}</p>
        )}
      </div>
    ) : (
      <Textarea
        className={cn(
          "h-80 min-h-80 overflow-y-auto field-sizing-fixed resize-none",
          textareaClassName
        )}
        disabled={disabled}
        id={textareaId}
        onChange={(event) => onChange?.(event.target.value)}
        onPaste={handleImagePaste}
        placeholder={textareaPlaceholder}
        readOnly={readOnly}
        value={value}
      />
    );
  } else {
    content = (
      <div
        className={cn(
          "h-80 min-h-80 overflow-y-auto rounded-xl border border-border bg-background px-4 py-3",
          previewClassName
        )}
      >
        {value.trim() ? (
          <MessageResponse components={components} mode="static">{value}</MessageResponse>
        ) : (
          <p className="text-sm text-muted-foreground">{emptyPreviewText}</p>
        )}
      </div>
    );
    if (onSelectionEdit) {
      content = <SelectionEditArea onSelectionEdit={onSelectionEdit}>{content}</SelectionEditArea>;
    }
  }

  let expandContent: React.ReactNode;

  if (expandMode === "split") {
    expandContent = (
      <div className="flex gap-3">
        <Textarea
          ref={expandTextareaRef}
          onScroll={syncExpandFromTextarea}
          className="min-h-[60vh] flex-1 overflow-y-auto field-sizing-fixed resize-none"
          disabled={disabled}
          onChange={(event) => onChange?.(event.target.value)}
          onPaste={handleImagePaste}
          placeholder={textareaPlaceholder}
          readOnly={readOnly}
          value={value}
        />
        <div
          ref={expandPreviewRef}
          onScroll={syncExpandFromPreview}
          className="min-h-[60vh] flex-1 overflow-y-auto rounded-xl border border-border bg-muted/30 px-4 py-3"
        >
          {value.trim() ? (
            <MessageResponse components={components} mode="static">{value}</MessageResponse>
          ) : (
            <p className="text-sm text-muted-foreground">{emptyPreviewText}</p>
          )}
        </div>
      </div>
    );
  } else if (expandMode === "markdown") {
    expandContent = (
      <Textarea
        className="min-h-[60vh] w-full overflow-y-auto field-sizing-fixed resize-none"
        disabled={disabled}
        onChange={(event) => onChange?.(event.target.value)}
        onPaste={handleImagePaste}
        placeholder={textareaPlaceholder}
        readOnly={readOnly}
        value={value}
      />
    );
  } else {
    expandContent = (
      <div className="min-h-[60vh] overflow-y-auto rounded-xl border border-border bg-background px-4 py-3">
        {value.trim() ? (
          <MessageResponse components={components} mode="static">{value}</MessageResponse>
        ) : (
          <p className="text-sm text-muted-foreground">{emptyPreviewText}</p>
        )}
      </div>
    );
  }

  return (
    <>
      {uploadingImage ? (
        <p className="mb-1.5 text-xs text-muted-foreground">Uploading image…</p>
      ) : null}
      <div className="group relative">
        {content}
        {expandable && (
          <Button
            aria-label="Open in full editor"
            className="absolute right-2 top-2 z-10 h-7 w-7 p-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
            onClick={() => setExpandOpen(true)}
            size="sm"
            type="button"
            variant="ghost"
          >
            <Maximize2 size={13} />
          </Button>
        )}
      </div>

      {expandable && (
        <Dialog open={expandOpen} onOpenChange={setExpandOpen}>
          <DialogContent className="sm:max-w-6xl">
            <DialogHeader>
              <DialogTitle>{expandTitle}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-1">
              <div className="flex justify-end">
                <MarkdownPreviewToggle mode={expandMode} onModeChange={setExpandMode} />
              </div>
              {expandContent}
            </div>
            <DialogFooter>
              <Button onClick={() => setExpandOpen(false)}>Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
